/**
 * VRG-726 — where a support request came FROM, for the internal queue's Source facet.
 *
 * VRG-721 made the app file quiet automatic reports for errors a user was shown, and links a
 * person's own report to the automatic one when they write in about the same error. A staffer
 * triaging the queue needs to tell three populations apart at a glance:
 *
 *   - **Auto (logged only)**: an automatic report nobody has said a word about. The error
 *     context is all there is; triage means reading the logs.
 *   - **Auto + user details**: one incident a person ALSO told us about. Either side of the
 *     link counts: the automatic report a person's request links to (or that they claimed with
 *     a note, VRG-427), and the person's request that links to one.
 *   - **User report**: a person wrote in, and no automatic report is involved.
 *
 * The link facts come from the server (`linkedUserReportCount`, `userNoteAt` on automatic rows;
 * `clientContext.linkedAutoReportId` on a person's row), never from the loaded page of the
 * queue, so a scope switch or a future page size cannot change a row's answer.
 *
 * ZERO app imports, so it is unit-tested under node (`test/support-queue-source.guard.spec.ts`).
 */

export type SupportReportSource = 'auto' | 'auto_with_user' | 'user'

export const SUPPORT_REPORT_SOURCE_VALUES: readonly SupportReportSource[] = [
  'auto',
  'auto_with_user',
  'user',
]

/**
 * The words. Colours live with every other chip palette in `constants/statuses.ts`
 * (`SUPPORT_REPORT_SOURCES`), which spreads these labels so the two cannot drift.
 */
export const SUPPORT_REPORT_SOURCE_LABELS: Record<SupportReportSource, string> = {
  auto: 'Auto (logged only)',
  auto_with_user: 'Auto + user details',
  user: 'User report',
}

/** The fields this module reads. A structural subset of `SupportRequest`, so tests need no fixtures. */
export interface SourceFacts {
  isSystemGenerated: boolean
  linkedUserReportCount?: number | null
  userNoteAt?: string | null
  clientContext: Record<string, unknown> | null
  occurrenceCount?: number | null
  createdAt: string
  updatedAt: string
  lastSeenAt?: string | null
}

const ctxOf = (request: SourceFacts): Record<string, unknown> =>
  request.clientContext && typeof request.clientContext === 'object' ? request.clientContext : {}

/** The automatic report a person's request is linked to, or null. Server-written (VRG-721). */
export const linkedAutoReportOf = (
  request: SourceFacts,
): { id: string; reference: number | null } | null => {
  if (request.isSystemGenerated) return null
  const ctx = ctxOf(request)
  const id = typeof ctx.linkedAutoReportId === 'string' ? ctx.linkedAutoReportId : null
  if (!id) return null
  const reference =
    typeof ctx.linkedAutoReportReference === 'number' ? ctx.linkedAutoReportReference : null
  return { id, reference }
}

export const supportReportSource = (request: SourceFacts): SupportReportSource => {
  if (request.isSystemGenerated) {
    const told = (request.linkedUserReportCount ?? 0) > 0 || Boolean(request.userNoteAt)
    return told ? 'auto_with_user' : 'auto'
  }
  return linkedAutoReportOf(request) ? 'auto_with_user' : 'user'
}

const REF_RE = /^[0-9a-f]{6,16}$/i

/**
 * The error refs on a request: the FIRST occurrence's (`clientContext.error.ref`, the sample the
 * report was filed with) and, on an automatic report that recurred, the LATEST occurrence's
 * (`clientContext.latestErrorRef`, server-written). `latest` is null when it equals `first`.
 */
export const errorRefsOf = (request: SourceFacts): { first: string | null; latest: string | null } => {
  const ctx = ctxOf(request)
  const error = ctx.error && typeof ctx.error === 'object' ? (ctx.error as Record<string, unknown>) : null
  const first = typeof error?.ref === 'string' && REF_RE.test(error.ref) ? error.ref : null
  const latestRaw =
    typeof ctx.latestErrorRef === 'string' && REF_RE.test(ctx.latestErrorRef) ? ctx.latestErrorRef : null
  return { first, latest: latestRaw && latestRaw !== first ? latestRaw : null }
}

/** The ref worth searching first: the latest occurrence's when there is one, else the first. */
export const searchableRefOf = (request: SourceFacts): string | null => {
  const { first, latest } = errorRefsOf(request)
  return latest ?? first
}

/**
 * The newest thing that happened on a request: created, replied to or re-statused
 * (`updatedAt` — every write path bumps it), or an automatic report recurring (`lastSeenAt`,
 * which also bumps `updatedAt`, read anyway so a row written by older code still sorts right).
 * The queue's default order.
 */
export const lastActivityAt = (request: SourceFacts): string => {
  let best = request.createdAt
  let bestMs = Date.parse(best)
  for (const candidate of [request.updatedAt, request.lastSeenAt]) {
    if (!candidate) continue
    const ms = Date.parse(candidate)
    if (Number.isFinite(ms) && (!Number.isFinite(bestMs) || ms > bestMs)) {
      best = candidate
      bestMs = ms
    }
  }
  return best
}

/**
 * The GCP project the API logs to. Dev and prod API services both log there (the service name
 * tells them apart in the results), so one link works for a ref from either.
 */
export const CLOUD_LOGGING_PROJECT = 'atlantean-depot-391316'

/**
 * A Cloud Logging search for one ref, as a quoted free-text query: it matches the structured
 * `jsonPayload.ref` field `ApplicationExceptionFilter` writes AND any text line that mentions the
 * ref, so it still finds the failure if the log shape changes.
 */
export const cloudLoggingUrl = (ref: string): string =>
  `https://console.cloud.google.com/logs/query;query=${encodeURIComponent(`"${ref}"`)}?project=${CLOUD_LOGGING_PROJECT}`
